import { isYes, yesOrNotSelectValidation } from 'helpers';
import { isValidNumber } from 'libphonenumber-js';
import {
  TypedForm,
  FormErrors as FormErrorsBase,
  ObjectSchema,
  string,
  object,
  date,
  email,
} from 'libs/form';
import { TFunc } from 'libs/i18n';
import { getTodayWithResetHours } from 'modules/onboarding/common/helpers/getToday';
import { Maybe } from 'types/maybe';
import { YesOrNoSelectType } from 'types/yesOrNo';

export interface CompanyGlobalFinancialMonitoringPartOneFormType {
  isSubjectToRegulation: Maybe<YesOrNoSelectType>;
  supervisoryAuthorityNameAndLocation?: string;

  mandatoryAmlRegulation?: {
    hasMandatoryAmlRegulations?: Maybe<YesOrNoSelectType>;
    regulationDetails?: string;
    authorizedBodyName?: string;
  };

  lastAmlAuditResult?: string;

  investigationsOrSanctions?: {
    hadInvestigationsOrSanctions?: Maybe<YesOrNoSelectType>;
    amlDecisionDateAndAuthorityInfo?: string;
  };

  internalAmlDocuments?: {
    hasInternalAmlDocuments?: Maybe<YesOrNoSelectType>;
    adoptionDate?: Date | null;
    lastUpdateDate?: Date | null;
    hasEffectivenessAssessmentProcedures?: Maybe<YesOrNoSelectType>;
    lastEffectivenessCheckInfo?: string;
    appliesToForeignAffiliates?: Maybe<YesOrNoSelectType>;
  };

  departmentsResponsibleForAmlFunctions?: {
    hasDepartmentsResponsibleForAmlFunctions?: Maybe<YesOrNoSelectType>;
    amlFunctionDepartmentsNames?: string;
  };

  responsibleAmlManager?: {
    hasResponsibleAmlManager?: Maybe<YesOrNoSelectType>;
    fullName?: string;
    position?: string;
    email?: string;
    phoneNumber?: string;
  };

  hasRiskManagementProcedures?: Maybe<YesOrNoSelectType>;
}

export const maxLengthTextField = 200;

export const getSchema = (
  t: TFunc
): ObjectSchema<CompanyGlobalFinancialMonitoringPartOneFormType> =>
  object({
    isSubjectToRegulation: yesOrNotSelectValidation.default(null).required(),

    supervisoryAuthorityNameAndLocation: string().when(
      'isSubjectToRegulation',
      {
        is: isYes,
        then: (schema) => schema.required().max(maxLengthTextField),
      }
    ),

    mandatoryAmlRegulation: object().when('isSubjectToRegulation', {
      is: isYes,
      then: () =>
        object()
          .default(null)
          .shape({
            hasMandatoryAmlRegulations: yesOrNotSelectValidation
              .default(null)
              .required(),

            regulationDetails: string().when('hasMandatoryAmlRegulations', {
              is: isYes,
              then: (schema) => schema.required(),
            }),
            authorizedBodyName: string().when('hasMandatoryAmlRegulations', {
              is: isYes,
              then: (schema) => schema.required(),
            }),
          })
          .required(),
    }),

    lastAmlAuditResult: string().when('isSubjectToRegulation', {
      is: isYes,
      then: (schema) => schema.max(maxLengthTextField),
    }),

    investigationsOrSanctions: object().when('isSubjectToRegulation', {
      is: isYes,
      then: () =>
        object({
          hadInvestigationsOrSanctions: yesOrNotSelectValidation
            .default(null)
            .required(),
          amlDecisionDateAndAuthorityInfo: string().when(
            'hadInvestigationsOrSanctions',
            {
              is: isYes,
              then: (schema) => schema.required().max(maxLengthTextField),
            }
          ),
        }).required(),
    }),

    internalAmlDocuments: object().when('isSubjectToRegulation', {
      is: isYes,
      then: () =>
        object({
          hasInternalAmlDocuments: yesOrNotSelectValidation
            .default(null)
            .required(),

          adoptionDate: date()
            .nullable()
            .default(null)
            .when('hasInternalAmlDocuments', {
              is: isYes,
              then: (schema) =>
                schema
                  .required()
                  .max(getTodayWithResetHours(), t('validation.maxDate')),
            }),
          lastUpdateDate: date()
            .nullable()
            .default(null)
            .when('hasInternalAmlDocuments', {
              is: isYes,
              then: (schema) =>
                schema
                  .required()
                  .max(getTodayWithResetHours(), t('validation.maxDate')),
            }),

          hasEffectivenessAssessmentProcedures: yesOrNotSelectValidation
            .default(null)
            .nullable()
            .when('hasInternalAmlDocuments', {
              is: isYes,
              then: (schema) => schema.required(),
            }),

          lastEffectivenessCheckInfo: string().when(
            'hasEffectivenessAssessmentProcedures',
            {
              is: isYes,
              then: (schema) => schema.required().max(maxLengthTextField),
            }
          ),

          appliesToForeignAffiliates: yesOrNotSelectValidation
            .default(null)
            .nullable(),
        }),
    }),

    departmentsResponsibleForAmlFunctions: object().when(
      'isSubjectToRegulation',
      {
        is: isYes,
        then: () =>
          object({
            hasDepartmentsResponsibleForAmlFunctions: yesOrNotSelectValidation
              .default(null)
              .required(),
            amlFunctionDepartmentsNames: string().when(
              'hasDepartmentsResponsibleForAmlFunctions',
              {
                is: isYes,
                then: (schema) => schema.required().max(maxLengthTextField),
              }
            ),
          }).required(),
      }
    ),
    responsibleAmlManager: object().when('isSubjectToRegulation', {
      is: isYes,
      then: () =>
        object({
          hasResponsibleAmlManager: yesOrNotSelectValidation
            .default(null)
            .required(),
          fullName: string().when('hasResponsibleAmlManager', {
            is: isYes,
            then: (schema) => schema.required().max(maxLengthTextField),
          }),
          position: string().when('hasResponsibleAmlManager', {
            is: isYes,
            then: (schema) => schema.required().max(maxLengthTextField),
          }),
          email: email(t).when('hasResponsibleAmlManager', {
            is: isYes,
            then: (schema) => schema.required(),
          }),
          phoneNumber: string().when('hasResponsibleAmlManager', {
            is: isYes,
            then: (schema) =>
              schema
                .test('phoneNumber', t('validation.phone'), (value) => {
                  if (!value) return true;
                  return isValidNumber(value);
                })
                .required()
                .max(20),
          }),
        }).required(),
    }),

    hasRiskManagementProcedures: yesOrNotSelectValidation.when(
      'isSubjectToRegulation',
      {
        is: isYes,
        then: (schema) => schema.default(null).required(),
        otherwise: (schema) => schema.default(null).nullable(),
      }
    ),
  });

export const { Field, Form, useFormContext, Submit } =
  TypedForm<CompanyGlobalFinancialMonitoringPartOneFormType>();

export type FormErrors =
  FormErrorsBase<CompanyGlobalFinancialMonitoringPartOneFormType>;
